/**
 * 型番が分からなかった登録済みの画像（Issue #929）: Claude が読んだが型番が決まらず、まだ型番が空のもの。手で型番を入れて保存する。
 * 候補はそのカードの収録パック（入力欄に触れたときに `GetCardSetCodes`）。保存したら一覧から消える。
 * 登録前の画像は上のスキャン済みの一覧の「要確認・読めない」で直す。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';
import CardRegionZoom, { CARD_REGIONS } from './CardRegionZoom';
import ZoomableImage from './ZoomableImage';
import RarityField, { raritiesFor } from './RarityField';

const PAGE = 50;
const STATUS_LABEL = { unmatched: '要確認', unreadable: '読めない' };

function UnresolvedRow({ row, onSaved }) {
  const [code, setCode] = useState(row.set_code ?? '');
  const [options, setOptions] = useState(null);
  const [rarity, setRarity] = useState('');
  const [busy, setBusy] = useState(false);
  const listId = `unresolved-set-codes-${row.card_image_id}`;

  const loadOptions = async () => {
    if (options) return;
    try {
      const codes = await api.project('GetCardSetCodes', { card_id: String(row.card_id) });
      setOptions(Array.isArray(codes) ? codes : []);
    } catch { setOptions([]); }
  };
  const save = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.project('SetCardImageSetCode', { card_image_id: row.card_image_id, set_code: code });
      if (rarity.trim()) await api.project('SetCardImageRarity', { card_image_id: row.card_image_id, rarity });
      onSaved(row.card_image_id);
    } catch (err) {
      alert('保存できませんでした: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" data-testid={`unresolved-${row.card_image_id}`}
      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <ZoomableImage src={row.download_url} alt={row.s3_key} testId={`unresolved-image-${row.card_image_id}`} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div className="card-meta">
          <strong>{row.card_name ?? '不明'}</strong>（{row.card_id}・{row.language_code} #{String(row.image_index).padStart(2, '0')}）
          — {STATUS_LABEL[row.status] ?? row.status}{row.note && `（${row.note}）`}
        </div>
        <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(e) => { e.preventDefault(); save(); }}>
          <CardRegionZoom src={row.download_url} region={CARD_REGIONS.setCode} width={280} label="型番" />
          <input className="form-input" style={{ width: 170 }} placeholder="型番（例 SD01-JP001）" list={listId}
            data-testid={`unresolved-code-${row.card_image_id}`}
            value={code} disabled={busy} onFocus={loadOptions} onChange={(e) => setCode(e.target.value)} />
          <datalist id={listId}>
            {(options ?? []).map((c) => <option key={c.set_code} value={c.set_code}>{c.pack_name ?? ''}</option>)}
          </datalist>
          <RarityField id={`unresolved-rarity-${row.card_image_id}`} testId={`unresolved-rarity-${row.card_image_id}`}
            value={rarity} disabled={busy} options={raritiesFor(options, code)} onChange={setRarity} />
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !code.trim()}>保存</button>
        </form>
      </div>
    </div>
  );
}

export default function UnresolvedSetCodes({ refreshKey, tempCount = 0 }) {
  const [rows, setRows] = useState([]);
  const [shown, setShown] = useState(PAGE);

  const load = useCallback(() => api.project('GetUnresolvedSetCodes')
    .then((r) => setRows(Array.isArray(r) ? r : []))
    .catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const drop = (id) => setRows((prev) => prev.filter((r) => r.card_image_id !== id));

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: '1rem', marginBottom: 4, color: '#aaa' }}>
        型番が分からなかった登録済みの画像 ({rows.length}件)
      </h2>
      <div className="card-meta" style={{ marginBottom: 12 }}>
        Claude が読んだが型番が決まらなかったものです。型番を入れて保存してください。
        {tempCount > 0 && ` 登録前の画像の ${tempCount} 件は、上の一覧の「要確認・読めない」で直せます。`}
      </div>
      {rows.length === 0 && <p className="loading">ありません</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.slice(0, shown).map((row) => <UnresolvedRow key={row.card_image_id} row={row} onSaved={drop} />)}
      </div>
      {rows.length > shown && (
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setShown((n) => n + PAGE)}>
          もっと見る（残り {rows.length - shown} 件）
        </button>
      )}
    </div>
  );
}
